// src/components/forums/ReplyList.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { parseLinks } from '@/lib/utils/parseLinks';
import { ForumReply } from '@/types/forum';
import { formatRelativeTime } from '@/lib/utils/time';
import Avatar from '@/components/ui/avatar/Avatar';
import { ThumbsUp, Edit, Trash, MoreHorizontal, X, ImagePlus } from 'lucide-react';
import {
  toggleReplyLike,
  deleteReply,
  updateReply as supabaseUpdateReply,
  getReplyLikeStatusBatch, // NEW
  getReplyLikeAvatars,
} from '@/lib/supabase/forum';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { ReportButton } from './ReportButton';
import ClubBadges from '../shared/ClubBadges';
import { getUserClubBadges } from '@/lib/supabase/clubs-utils';
import type { ClubBadge } from '@/lib/supabase/clubs-utils';
import { supabase } from '@/lib/supabase';
import type { Member } from '@/types/user';

// fallback if a user has no avatar_url
const DEFAULT_AVATAR =
  'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png';


// Character threshold for collapsing content
const COLLAPSE_THRESHOLD = 300;

interface ReplyListProps { 
  replies: ForumReply[];
  setReplies: React.Dispatch<React.SetStateAction<ForumReply[]>>;
  onDeleteReply?: (replyId: string) => void;
  onUpdateReply?: (replyId: string, content: string, media?: string | null, video?: string | null) => Promise<boolean>;
}

const ReplyList: React.FC<ReplyListProps> = ({
  replies,
  setReplies,
  onDeleteReply, 
  onUpdateReply,
}) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();

  // NEW: fetch all users for mention lookup
  const [allUsers, setAllUsers] = useState<Member[]>([]);
  useEffect(() => {
    const fetchUsers = async () => {
      const { data } = await supabase
        .from('users')
        .select('id, auth_id, full_name');
      if (data) setAllUsers(data);
    };
    fetchUsers();
  }, []);

  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [authorClubMap, setAuthorClubMap] = useState<Record<string, ClubBadge[]>>({});

  const [editContent, setEditContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [collapsedReplies, setCollapsedReplies] = useState<Record<string, boolean>>({});
  const [selectedMedia, setSelectedMedia] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [removeMedia, setRemoveMedia] = useState(false);

// right after the other useState hooks
type LikeAvatar = { auth_id: string; full_name: string; avatar_url: string | null };

const [replyAvatars, setReplyAvatars] = useState<Record<string, LikeAvatar[]>>({});

// fetch when `replies` change
useEffect(() => {
  if (replies.length === 0) return;

  let alive = true;

  (async () => {
    try {
      const pairs = await Promise.all(
        replies
          .filter(r => r.likes > 0)                      // skip zero-like replies
          .map(async r => {
            const avatars = await getReplyLikeAvatars(r.id, r.isClub);
            return [r.id, avatars] as const;
          })
      );

      if (!alive) return;
      setReplyAvatars(Object.fromEntries(pairs));
    } catch (err) {
      console.error('Avatar-fetch error:', err);
    }
  })();

  return () => { alive = false; };
}, [replies]);

  
  // Initialize collapsed state for long replies
  useEffect(() => {
    const initialCollapsedState: Record<string, boolean> = {};
    replies.forEach(reply => {
      if (reply.content.length > COLLAPSE_THRESHOLD) {
        initialCollapsedState[reply.id] = true;
      }
    });
    setCollapsedReplies(initialCollapsedState);
  }, [replies]);

/* ─── Load “liked by me” status so colours/counts are correct ─── */
useEffect(() => {
  if (!user || replies.length === 0) return;

  let alive = true;

  (async () => {
    try {
      const statusArr = await getReplyLikeStatusBatch(
        replies.map(r => r.id),
        user.id
      ); // returns [{ id: '...', liked: true | false }]

      if (!alive) return;

      const likedMap = Object.fromEntries(
        statusArr.map(s => [s.id, s.liked])
      );

      setReplies(prev =>
        prev.map(r =>
          likedMap[r.id] !== undefined ? { ...r, isLiked: likedMap[r.id] } : r
        )
      );
    } catch (err) {
      console.error('Error loading reply like status:', err);
    }
  })();

  return () => {
    alive = false;
  };
}, [user?.id, replies.length]);

useEffect(() => {
  const fetchAllClubBadges = async () => {
    const map: Record<string, ClubBadge[]> = {};
    const seen = new Set<string>();

    for (const reply of replies) {
      const userId = reply.author.id;
      if (!userId || seen.has(userId)) continue;
      seen.add(userId);

      try {
        const clubs = await getUserClubBadges(userId);
        map[userId] = clubs;
      } catch {
        console.warn(`Failed to fetch clubs for ${userId}`);
      }
    }

    setAuthorClubMap(map);
  };

  fetchAllClubBadges();
}, [replies]);




  const toggleReplyCollapse = (replyId: string) => {
    setCollapsedReplies(prev => ({
      ...prev,
      [replyId]: !prev[replyId]
    }));
  };

  const handleLike = (replyId: string, index: number) => {
  // 1️⃣ optimistic update – flip the UI instantly
  setReplies(prev => {
    const updated = [...prev];
    const r = updated[index];

    updated[index] = {
      ...r,
      isLiked: !r.isLiked,
      likes:   r.likes + (r.isLiked ? -1 : 1),  // keep the counter in-sync
    };
    return updated;
  });

  // 2️⃣ background network call
  toggleReplyLike(replyId).catch(() => {
    // 3️⃣ rollback if Supabase rejects the mutation
    setReplies(prev => {
      const updated = [...prev];
      const r = updated[index];

      updated[index] = {
        ...r,
        isLiked: !r.isLiked,
        likes:   r.likes + (r.isLiked ? -1 : 1),
      };
      return updated;
    });
  });
};

  const handleEditClick = (reply: ForumReply) => {
    setEditingReplyId(reply.id);
    setEditContent(reply.content);
    setSelectedMedia(null);
    setMediaPreview(null);
    setRemoveMedia(false);
  };

  const handleCancelEdit = () => {
    setEditingReplyId(null);
    setEditContent('');
    setSelectedMedia(null);
    setMediaPreview(null);
    setRemoveMedia(false);
  };

  const handleSaveEdit = async (replyId: string, reply: ForumReply) => {
    if (!editContent.trim()) return;

    setIsSubmitting(true);
    try {
      // Prepare update data
      const updateData: any = {
        content: editContent
      };
      
      // Handle media changes
      if (removeMedia) {
        updateData.media_url = null;
        updateData.video_url = null;
      } else if (selectedMedia) {
        // Upload new media
        const isVideo = selectedMedia.type.startsWith('video/');
        const bucket = isVideo ? 'videos' : 'social-feed';
        
        // Generate unique filename
        const timestamp = Date.now();
        const extension = selectedMedia.name.split('.').pop()?.toLowerCase() || (isVideo ? 'mp4' : 'jpg');
        const sanitizedName = selectedMedia.name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
        
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData?.user?.id;
        
        if (!userId) throw new Error("User not authenticated");
        
        const filePath = `user_directory/${userId}/${timestamp}-${sanitizedName}.${extension}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(filePath, selectedMedia, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) throw uploadError;
        
        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from(bucket)
          .getPublicUrl(uploadData.path);
        
        if (isVideo) {
          updateData.video_url = publicUrl;
          updateData.media_url = null;
        } else {
          updateData.media_url = publicUrl;
          updateData.video_url = null;
        }
      }
      
      // Call the parent component's update function
      const success = await onUpdateReply?.(
        replyId, 
        editContent, 
        updateData.media_url !== undefined ? updateData.media_url : reply.media,
        updateData.video_url !== undefined ? updateData.video_url : reply.video
      );
      
      if (success) {
        // Update local state
        setReplies(prev =>
          prev.map(r =>
            r.id === replyId
              ? {
                  ...r,
                  content: editContent,
                  isEdited: true,
                  updatedAt: new Date().toISOString(),
                  media: updateData.media_url !== undefined ? updateData.media_url : r.media,
                  video: updateData.video_url !== undefined ? updateData.video_url : r.video
                }
              : r
          )
        );
        setEditingReplyId(null);
        setEditContent('');
        setSelectedMedia(null);
        setMediaPreview(null);
        setRemoveMedia(false);
      }
    } catch (err) {
      console.error('Error saving edited reply:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    setIsSubmitting(true);
    try {
      const success = await deleteReply(replyId);
      if (success) {
        setReplies(prev => prev.filter(r => r.id !== replyId));
        if (onDeleteReply) onDeleteReply(replyId);
      }
    } catch (err) {
      console.error('Error deleting reply:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatContent = (content: string) =>
    content.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {parseLinks(line)}
        <br />
      </React.Fragment>
    ));

  // NEW: renderContent replaces @mentions with links
 const renderContent = (text: string) => {
  // while users are loading, fallback to your newline+URL parser
  if (allUsers.length === 0) {
    return text.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {parseLinks(line)}
        <br />
      </React.Fragment>
    ));
  }

  // regex that captures @Name or @Name LastName as a single token
  const mentionRegex = /(@[A-Za-zÀ-ÖØ-öø-ÿ]+(?: [A-Za-zÀ-ÖØ-öø-ÿ]+)*)/g;

  return text.split(mentionRegex).map((part, i) => {
    // if this chunk matches our @mention pattern…
    if (mentionRegex.test(part)) {
      const name = part.slice(1);         // drop the '@'
      const m = allUsers.find(u => u.full_name === name);
      if (m) {
        return (
          <Link
            key={i}
            to={`/dashboard/members/${m.id}`}
            className="text-gold-400"
          >
            @{name}
          </Link>
        );
      }
    }
    // otherwise just parse URLs/newlines
    return (
      <React.Fragment key={i}>
        {parseLinks(part)}
      </React.Fragment>
    );
  });
};


  return (
    <div className="space-y-4 w-full">
     {replies.map((reply, index) => (
  <div
    id={`reply-${reply.id}`}
    key={reply.id}
    className={cn(
      'rounded-xl border overflow-hidden hover:border-gold-400 transition-all duration-300',
      isDarkMode ? 'bg-dark-200 border-dark-300' : 'bg-light-200 border-light-300'
    )}
  >
    {/* … */}

          {/* Header */}
          <div
            className={cn(
              'p-4 border-b flex items-center justify-between',
              isDarkMode ? 'border-dark-300' : 'border-light-300'
            )}
          >
            <div className="flex items-center gap-3">
              <Avatar
                src={reply.author.image}
                alt={reply.author.name}
                size={32}
                onClick={() => navigate(`/dashboard/members/${reply.author.id}`)}
                className="cursor-pointer"
              />
              <div>
                <div className="flex flex-col text-left">
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      navigate(`/dashboard/members/${reply.author.id}`);
                    }}
                    className={cn(
                      'font-medium hover:text-gold-400 transition-colors mb-1 p-0 text-left',
                      isDarkMode ? 'text-white' : 'text-light-900'
                    )}
                  >
                    {reply.author.name}
                  </button>
<ClubBadges clubs={authorClubMap[reply.author.id] || []} size="md" limit={10} />




                </div>
                <div
                  className={cn(
                    'text-xs',
                    isDarkMode ? 'text-white/70' : 'text-light-600'
                  )}
                >
                  {reply.author.city && `${reply.author.city} • `}
                  {formatRelativeTime(reply.createdAt)}
                  {reply.isEdited && ' (edited)'}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2">
            {/* Like */}
<div className="flex items-center gap-2">
  <button
    onClick={e => {
      e.stopPropagation();
      handleLike(reply.id, index);
    }}
    className={cn(
      'p-1.5 rounded-full transition-colors',
      reply.isLiked
        ? 'text-gold-400 bg-gold-400/10'
        : 'text-dark-800 hover:text-white hover:bg-dark-300'
    )}
    aria-label={reply.isLiked ? 'Unlike' : 'Like'}
  >
    <ThumbsUp className="h-4 w-4" />
  </button>

  {/* avatar stack */}
  {reply.likes > 0 && (
    <div className="flex items-center -space-x-2">
      {(replyAvatars[reply.id] ?? []).slice(0, 4).map(a => (
        <img
          key={a.auth_id}
          src={a.avatar_url ?? DEFAULT_AVATAR}
          title={a.full_name}
          className="w-6 h-6 rounded-full border-2 border-white"
        />
      ))}
      {reply.likes > 4 && (
        <span className="text-xs pl-1">
          +{reply.likes - 4}
        </span>
      )}
    </div>
  )}
</div>


              {/* Edit/Delete menu */}
              {user?.id === reply.author.id ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal
                        className={cn(
                          'h-4 w-4',
                          isDarkMode ? 'text-white' : 'text-dark-800'
                        )}
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className={cn(
                      isDarkMode ? 'bg-dark-300 border-dark-400' : 'bg-white border-gray-200'
                    )}
                  >
                    <DropdownMenuItem asChild>
                      <button
                        onClick={() => handleEditClick(reply)}
                        className={cn(
                          'w-full flex items-center px-2 py-1 cursor-pointer',
                          isDarkMode ? 'hover:bg-dark-400 text-white' : 'hover:bg-gray-100 text-gray-700'
                        )}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Reply
                      </button>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleDeleteReply(reply.id);
                        }}
                        className={cn(
                          'w-full flex items-center px-2 py-1 text-red-500 cursor-pointer',
                          isDarkMode ? 'hover:bg-dark-400' : 'hover:bg-gray-100'
                        )}
                      >
                        <Trash className="h-4 w-4 mr-2" />
                        Delete Reply
                      </button>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <ReportButton
                  contentId={reply.id}
                  contentType="reply"
                  variant="icon"
                />
              )}
            </div>
          </div>

{/* Body */}
{editingReplyId === reply.id ? (
  <div className="p-4">
    <div className="space-y-4">
      <Textarea
        value={editContent}
        onChange={e => setEditContent(e.target.value)}
        className={cn(
          'min-h-[120px]',
          isDarkMode
            ? 'bg-dark-300 border-dark-400 text-white'
            : 'bg-dark-200 border-light-300 text-light-900'
        )}
        disabled={isSubmitting}
      />
      
      {/* Media Management */}
      <div className="space-y-2">
        {/* Current Media Preview */}
        {!removeMedia && ((reply.media || reply.video) || mediaPreview) && (
          <div className="relative rounded-lg overflow-hidden border border-dark-400 mb-4">
            {mediaPreview ? (
              selectedMedia?.type.startsWith('video/') ? (
                <video 
                  src={mediaPreview} 
                  className="max-h-64 w-auto mx-auto" 
                  controls
                />
              ) : (
                <img 
                  src={mediaPreview} 
                  alt="New media preview" 
                  className="max-h-64 w-auto mx-auto" 
                />
              )
            ) : (reply.video ? (
              <video 
                src={reply.video} 
                className="max-h-64 w-auto mx-auto" 
                controls
              />
            ) : reply.media && (
              <img 
                src={reply.media} 
                alt="Current media" 
                className="max-h-64 w-auto mx-auto" 
              />
            ))}
            <button
              type="button"
              onClick={() => {
                if (mediaPreview) {
                  setSelectedMedia(null);
                  setMediaPreview(null);
                } else {
                  setRemoveMedia(true);
                }
              }}
              className="absolute top-2 right-2 p-1 rounded-full bg-dark-900/80 text-white hover:bg-dark-900"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        
        {/* Media Actions */}
        <div className="flex flex-wrap gap-2">
          {removeMedia && (reply.media || reply.video) && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setRemoveMedia(false)}
              className={cn(
                "text-sm",
                isDarkMode ? "border-dark-400 text-white hover:bg-dark-400" : "border-light-300 text-light-900 hover:bg-light-200"
              )}
            >
              Restore Original Media
            </Button>
          )}
          
          {!mediaPreview && (removeMedia || !(reply.media || reply.video)) && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*,video/*';
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    setSelectedMedia(file);
                    setRemoveMedia(false);
                    
                    // Create preview
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setMediaPreview(reader.result as string);
                    };
                    reader.readAsDataURL(file);
                  }
                };
                input.click();
              }}
              className={cn(
                "text-sm",
                isDarkMode ? "border-dark-400 text-white hover:bg-dark-400" : "border-light-300 text-light-900 hover:bg-light-200"
              )}
            >
              <ImagePlus className="h-4 w-4 mr-2" />
              {(reply.media || reply.video) ? 'Replace Media' : 'Add Media'}
            </Button>
          )}
        </div>
      </div>
      
      <div className="flex justify-end space-x-2">
        <Button
          onClick={handleCancelEdit}
          variant="outline"
          disabled={isSubmitting}
          className={cn(
            'h-9',
            isDarkMode
              ? 'border-dark-400 text-white hover:bg-dark-400'
              : 'border-gray-300 text-gray-700 hover:bg-gray-100'
          )}
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleSaveEdit(reply.id, reply)}
          disabled={isSubmitting || !editContent.trim()}
          className="h-9 bg-gold-400 hover:bg-gold-500 text-dark-50"
        >
          {isSubmitting ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
    </div>
  </div>
) : (
  <div className="p-4">
    <div
      className={cn(
        'prose max-w-none break-words',
        isDarkMode ? 'prose-invert text-white' : 'text-light-900'
      )}
    >
      {renderContent(reply.content)}
    </div>
    

    
    {reply.media && (
      <div className="mt-4">
        <img
          src={reply.media}
          alt="Reply attachment"
          className="max-h-96 rounded-lg mx-auto"
          loading="lazy"
        />
      </div>
    )}

    {reply.video && (
      <div className="mt-4">
        <video
          src={reply.video}
          controls
          className="max-h-96 w-full rounded-lg"
          preload="metadata"
        />
      </div>
    )}
  </div>
)}

        </div>
      ))}
    </div>
  );
};

export default ReplyList;